import { externalLinks } from 'lib/consts/externalLinks'
import { getMailtoHref } from 'lib/navigation/helpers/getMailtoHref/getMailtoHref'
import { t } from 'lib/translations/i18next'
import * as clipboard from 'lib/clipboard'
import { useSnackbarsStore } from 'settle-ui/hooks/useSnackbarsStore/useSnackbarsStore'
import { INTERCOM_CUSTOM_LAUNCHER_ID } from 'routes/components/AppLayout/components/AppLayoutContent/components/StickyButtonTray/components/IntercomCustomLauncherFab/IntercomCustomLauncherFab'

export type ContactMethod = 'email' | 'Intercom'

export interface OpenSupportContactParams {
  /** Recipient when chat is unavailable, `support@` unless given. Never sent to Intercom. */
  email?: string
  /** Email subject used when chat is unavailable. Never sent to Intercom. */
  subject?: string
  message?: string
  afterContact?: (method: ContactMethod) => void
}

const handleEmailFallback = async ({
  email = externalLinks.supportEmail,
  subject,
  message,
  afterContact,
}: OpenSupportContactParams) => {
  const mailto = getMailtoHref({ to: email, subject, body: message })

  window.open(mailto, '_blank', 'noopener,noreferrer')
  afterContact?.('email')

  const clipboardSucceeded = await clipboard.write(email)

  const snackbarMessage = clipboardSucceeded
    ? t('openSupportContact::chatUnavailableEmailCopied_v2', { email })
    : t('openSupportContact::chatUnavailableEmail_v2', { email })

  useSnackbarsStore.getState().showAlert(snackbarMessage, { timeout: 15000 })
}

/**
 * `startConversation` sends the moment it is called, so it must never run without a message: an
 * empty send posts a blank message and strands the payer in a workflow that then hides its own
 * topic buttons and its composer.
 */
export const openSupportContact = async (params: OpenSupportContactParams = {}) => {
  if (document.getElementById(INTERCOM_CUSTOM_LAUNCHER_ID) === null) {
    await handleEmailFallback(params)
    return
  }

  try {
    if (params.message) {
      window.Intercom('startConversation', params.message, { appendToLastConversation: true })
    } else {
      window.Intercom('showNewMessage')
    }

    params.afterContact?.('Intercom')
  } catch {
    await handleEmailFallback(params)
  }
}
